if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.selectorMapping = {
          day: '[data-timer-day]',
          hour: '[data-timer-hour]',
          minute: '[data-timer-minute]',
          second: '[data-timer-second]',
        };

        this.units = ['day', 'hour', 'minute', 'second'];
        this.type = this.dataset.type;
        this.duration = this.dataset.duration;
        this.loop = false;
        this.shouldAddLeadingZero = false;

        this.initCountdown();
      }

      initCountdown() {
        switch (this.type) {
          case 'fixed_time': {
            const countdownData = this.dataset.dueDate;

            if (countdownData) {
              this.targetTime = new Date(countdownData).getTime();
            }

            break;
          }

          default:
            this.targetTime = this.getEvergreenDueDate().getTime();
            break;
        }

        if (!this.targetTime) {
          return;
        }

        this.startTime = Date.now();

        if (this.targetTime > this.startTime) {
          this.fetchDomElements();
          this.beginCountdown();
        }
      }

      fetchDomElements() {
        this.elements = {};

        this.units.forEach((unit) => {
          this.elements[unit] = this.querySelector(this.selectorMapping[unit]);
        });
      }

      beginCountdown() {
        this.intervalId = setInterval(() => {
          const remainingTime = this.targetTime - Date.now();

          if (remainingTime <= 0) {
            this.terminateCountdown();
          } else {
            this.updateDisplay(remainingTime);
          }
        }, 1000);

        this.classList.remove('hidden');
      }

      updateDisplay(remainingTime) {
        const timeComponents = this.computeTimeComponents(remainingTime);

        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent =
              this.formatTimeComponent(timeComponents[unit]);
          }
        });
      }

      computeTimeComponents(ms) {
        return {
          day: Math.floor(ms / this.MS_IN_DAY),
          hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
          minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
          second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
        };
      }

      formatTimeComponent(value) {
        return this.shouldAddLeadingZero && value < 10
          ? `0${value}`
          : value.toString();
      }

      terminateCountdown() {
        clearInterval(this.intervalId);

        if (this.loop) {
          this.beginCountdown();
        } else {
          this.classList.add('hidden');
        }
      }

      resetCountdown() {
        clearInterval(this.intervalId);

        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = '00';
          }
        });
      }

      getEvergreenDueDate() {
        let intervalHrs;

        const now = new Date();
        const dueDate = new Date(now);

        switch (this.duration) {
          case 'every_month':
            dueDate.setMonth(dueDate.getMonth() + 1);
            dueDate.setDate(1);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;

          case 'every_week': {
            const londonFormatter = new Intl.DateTimeFormat('en-GB', {
              timeZone: 'Europe/London',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              weekday: 'short',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: false,
            });

            const parts = Object.fromEntries(
              londonFormatter
                .formatToParts(now)
                .filter((part) => part.type !== 'literal')
                .map((part) => [part.type, part.value])
            );

            const weekdayMap = {
              Sun: 0,
              Mon: 1,
              Tue: 2,
              Wed: 3,
              Thu: 4,
              Fri: 5,
              Sat: 6,
            };

            const londonDay = weekdayMap[parts.weekday];
            const londonHour = Number(parts.hour);

            let daysUntilMonday = (8 - londonDay) % 7;

            if (londonDay === 1) {
              daysUntilMonday = londonHour < 12 ? 0 : 7;
            }

            const londonDate = new Date(
              Date.UTC(
                Number(parts.year),
                Number(parts.month) - 1,
                Number(parts.day) + daysUntilMonday,
                12,
                0,
                0
              )
            );

            const offsetFormatter = new Intl.DateTimeFormat('en-GB', {
              timeZone: 'Europe/London',
              timeZoneName: 'longOffset',
            });

            const offsetPart = offsetFormatter
              .formatToParts(londonDate)
              .find((part) => part.type === 'timeZoneName');

            const offsetMatch = offsetPart?.value.match(
              /GMT([+-])(\d{2}):(\d{2})/
            );

            let offsetMs = 0;

            if (offsetMatch) {
              const sign = offsetMatch[1] === '+' ? 1 : -1;
              const hours = Number(offsetMatch[2]);
              const minutes = Number(offsetMatch[3]);

              offsetMs = sign * (hours * 60 + minutes) * 60 * 1000;
            }

            return new Date(londonDate.getTime() - offsetMs);
          }

          case 'every_2_hrs':
            intervalHrs = 2;
            break;

          case 'every_6_hrs':
            intervalHrs = 6;
            break;

          case 'every_12_hrs':
            intervalHrs = 12;
            break;

          case 'every_24_hrs':
            intervalHrs = 24;
            break;
        }

        const currentHour = now.getHours();

        const startPoints = [];

        for (let i = 0; i < 24; i += intervalHrs) {
          startPoints.push(i);
        }

        for (let i = 0; i < startPoints.length; i++) {
          if (
            currentHour < startPoints[i] ||
            (currentHour === startPoints[i] &&
              now.getMinutes() === 0 &&
              now.getSeconds() === 0)
          ) {
            dueDate.setHours(startPoints[i], 0, 0, 0);
            return dueDate;
          }
        }

        dueDate.setDate(dueDate.getDate() + 1);
        dueDate.setHours(startPoints[0], 0, 0, 0);

        return dueDate;
      }
    }
  );
}